import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { propOr } from 'ramda';
import styled from 'styled-components';

import { DisplayFlexS } from '../../../utils/stylesHelpers/index';
import { getScreenSize, getThemeColor, getThemeFont, getThemeSize } from '../../../utils/theme';
import arrowIcon from '../../assets/imgs/icons/arrowProgress_Icon.svg';
import DeleteCrossIcon from '../../assets/imgs/icons/DeleteCrossIcon.svg';
import DefaultEmployeeImg from '../../assets/imgs/icons/employeeIcon.svg';
import AddIcon from '../../assets/imgs/icons/icon_add.svg';
import AddPictureDisabledIcon from '../../assets/imgs/icons/icon_add_picture-disabled.svg';
import AddPictureIcon from '../../assets/imgs/icons/icon_add_picture.svg';
import CloseIcon from '../../assets/imgs/icons/icon_close.svg';
import NoLogoIcon from '../../assets/imgs/icons/NOlogo_Icon.svg';
import NoLogoMobile from '../../assets/imgs/icons/NoLogoMobile.svg';
import { SelectS, Text } from '../common';
import { CompanyInfoRowS } from '../company/companyItemS';

export const ContentWrapS = styled.div`
  max-width: ${getScreenSize(['tabletPC'])};
  width: 100%;
  margin: ${({ isAdmin }) => (isAdmin ? 'unset' : '30px auto 0')};

  @media (max-width: ${getScreenSize(['tabletPC'])}) {
    width: 70%;
  }

  @media (max-width: ${getScreenSize(['medium'])}) {
    width: 100%;
    padding: 0;
    margin: 0;
  }
`;

export const SettingsListWrapS = styled.ul`
  padding: 20px 50px 20px 20px;
  flex-basis: 35%;
  border-right: 2px solid ${getThemeColor(['grayBlue'])};
`;

export const AdvertismentBlockS = styled.div`
  margin: 30px 0 40px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    margin: 10px 0 20px;
  }
`;

export const SettingsListItemS = styled.li`
  position: relative;
  padding: 20px;
  font-weight: bold;
  font-family: ${getThemeFont(['bold'])};
  font-size: ${getThemeSize(['bigText'])};
  color: ${({ selected }) => (selected ? getThemeColor(['violet']) : `rgba(29, 29, 108, 0.75)`)};
  border-bottom: 2px solid ${getThemeColor(['grayBlue'])};

  &:hover {
    cursor: pointer;
  }

  ${({ selected }) =>
    selected &&
    `&:before {
    display: block;
    position: absolute;
    content: ' ';
    left: 0;
    top: 0;
    transform: translateY(35%);
    width: 5px;
    height: 60%;
    background-color: #1d1d6c;
  }`}
`;

export const SettingsContentWrapS = styled.div`
  width: 100%;
  padding: 30px 50px 0 50px;

  @media (max-width: ${getScreenSize(['tabletPC'])}) {
    padding-left: 20px;
    padding-right: 10px;
  }
`;

const mediumScreenStyles = `
  font-family: ${getThemeFont(['regular'])};
  font-size: ${getThemeSize(['mediumText'])};
  line-height: 15.6px;
`;

export const CaptureWrapS = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  margin: 30px 0;
  font-size: ${getThemeSize(['middleText'])};
  font-weight: 700;
  font-family: ${getThemeFont(['bold'])};
  border-bottom: 2px solid ${getThemeColor(['grayBlue'])};

  & span {
    font-size: ${getThemeSize(['middleText'])};

    &:first-of-type {
      margin-right: 15px;
    }
  }

  @media (max-width: ${getScreenSize(['medium'])}) {
    align-items: flex-start;
    margin: 10px 0 20px;
    padding: 0 22px 6px;
    ${mediumScreenStyles}
    & span {
      text-align: left;
      ${mediumScreenStyles}
      &:first-of-type {
        margin-right: 7px;
      }
    }
  }
`;

export const CompanyLogoWrapS = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-right: 20px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-right: 16px;
    position: relative;
  }
`;

export const LogoWithUploadOverlayS = styled.div`
  margin: 0 auto auto;

  width: 100px;
  height: 100px;

  border-radius: 50%;
  border: 1px solid ${getThemeColor(['grayBlue'])};
  background-color: ${getThemeColor(['white'])};
  background-image: url(${({ logo }) => logo || NoLogoIcon});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: ${({ logo }) => (logo ? 'contain' : '50%')}; // 50% it is for default entity

  display: flex;
  justify-content: center;
  align-items: center;

  cursor: pointer;

  &:hover {
    transition: background-color 1s linear, background-image 1s linear;
    background-color: ${getThemeColor(['greyBlueLight'])};
    background-image: none;

    color: ${getThemeColor(['greyViolet'])};
    font-size: ${getThemeSize(['bigText'])};
    text-align: center;

    &:after {
      content: '${t('logoText.changeLogo')}';
    }
  }

  @media (max-width: ${getScreenSize(['medium'])}) {
    width: 63px;
    height: 63px;
    background-image: url(${({ logo }) => logo || NoLogoMobile});
  }
`;

export const CompanyVerifiedRow = styled(CompanyInfoRowS)`
  align-items: center;

  & img {
    margin-right: 10px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    flex-direction: row;
    align-items: center;
    margin-top: 4px;
    & img {
      margin-right: 4px;
      width: 15px;
      height: 15px;
    }
    & span {
      font-family: ${getThemeFont(['regular'])};
      line-height: 14.4px;
      font-size: ${getThemeSize(['smallText'])};
    }
  }
`;

export const CompanyInfoRowEditS = styled(CompanyInfoRowS)`
  & span {
    font-size: 24px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    & span {
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleText'])};
      font-weight: 700;
    }
  }
`;

export const InputWrapper = styled.div`
  flex-basis: ${propOr('100%', 'width')};
  width: ${propOr('100%', 'width')};
  max-width: ${propOr('100%', 'maxWidth')};
  margin-bottom: 10px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-bottom: 5px;
    flex-basis: ${propOr('100%', 'mobileWidth')};
    width: 100%;
    & input {
      font-size: ${getThemeSize(['middleSmallText'])};
    }
  }
`;

export const DataRowWrapS = styled(DisplayFlexS)`
  margin-bottom: 20px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    flex-direction: column;

    & input {
      margin-bottom: 19px;
    }
  }
`;

export const DataRowItemsWrapS = styled(DataRowWrapS)`
  align-items: flex-start;
  justify-content: space-between;
`;

export const CommonMultipleFieldWrap = styled.div`
  margin-bottom: 10px;
`;

export const AddNewFieldBtnWrapS = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  margin-top: ${({ marginTopScreenDesktop }) => marginTopScreenDesktop && marginTopScreenDesktop};

  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-top: ${({ marginTopScreenMedium }) => marginTopScreenMedium && marginTopScreenMedium};
  }
  &:hover {
    cursor: pointer;
  }

  &.employees {
    & span:nth-child(1) {
      font-size: ${getThemeSize(['middleText'])};
      margin: 5px;
    }
    & span:nth-child(2) {
      font-size: ${getThemeSize(['smallText'])};
    }

    @media (max-width: ${getScreenSize(['medium'])}) {
      margin-bottom: 50px;
      & span:nth-child(1) {
        font-size: ${getThemeSize(['middleText'])};
        margin: 0 8px 0 0;
      }
      & span:nth-child(2) {
        font-size: ${getThemeSize(['middleSmallText'])};
      }
    }
  }
`;

export const EmproyeeEditWrapperS = styled.div`
  & select {
    height: auto;
    margin-bottom: 27px;
  }

  & input {
    margin-bottom: 27px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    & span {
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleSmallText'])};
      font-weight: 400;
      line-height: 13.2px;
      text-align: left;
    }
    & select {
      margin-bottom: 19px;
      padding: 8px 24px 8px 12px;
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleSmallText'])};
      font-weight: 400;
    }
    & input {
      margin-bottom: 19px;
      padding: 8px 12px 7px 12px;
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleSmallText'])};
    }
  }
`;

export const BaseEditCompanyBlockFooterS = styled.div`
  height: 70px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  background-color: ${getThemeColor(['greyBlueLight'])};
  border-radius: 0 0 3px 3px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    padding-right: 0;
    justify-content: center;
  }
`;

export const CompanyHistory = styled.div``;

export const CompanyHistoryRecord = styled.div`
  margin-bottom: 20px;
`;

export const CompanyHistoryTitle = styled(Text)`
  display: inline-block;
  margin-right: 10px;
`;

export const CompanyHistoryDate = styled.span`
  color: ${getThemeColor(['greyDark'])};
`;

export const MainSectionS = styled.div`
  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-top: 15px;
    width: 100%;
  }
`;

// FulfillmentProgressBar

export const FulfillmentWrS = styled.div`
  max-width: 235px;
  width: 100%;
  margin: 0 auto 20px;
  min-height: 90px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    max-width: 235px;
    margin: 0 auto 12px;
  }
`;

export const FulfillmentCurrentS = styled(Text)`
  display: block;
  margin-bottom: 10px;
  @media (max-width: ${getScreenSize(['medium'])}) {
    font-family: ${getThemeFont(['regular'])};
    font-size: ${getThemeSize(['smallText'])};
    font-weight: 400;
    line-height: 14.4px;
    text-align: center;
    color: ${getThemeColor(['primaryDark'])};
  }
`;

export const FulfillmentCurrentNumberS = styled.strong`
  color: ${getThemeColor(['violet'])};
  font-weight: 700;
  @media (max-width: ${getScreenSize(['medium'])}) {
    font-size: ${getThemeSize(['smallText'])};
    color: ${getThemeColor(['primaryDark'])};
  }
`;

export const FulfillmentProgressBarWrS = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  overflow: hidden;
  background-color: ${getThemeColor(['grayBlue'])};
  @media (max-width: ${getScreenSize(['medium'])}) {
    height: 7px;
  }
`;

export const FulfillmentProgressBarS = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background-color: ${getThemeColor(['red'])};
  ${({ withProgressBar }) =>
    withProgressBar &&
    `
    width: ${withProgressBar}%
  `};
`;

export const FulfillmentCompleteS = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 25px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-top: 22px;
    justify-content: center;
  }
`;

export const FulfillmentCompleteTextS = styled(Text)`
  position: relative;
  &:before {
    content: '';
    position: absolute;
    top: -10px;
    left: -50px;
    width: 60px;
    height: 40px;
    background: url(${arrowIcon}) no-repeat;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    font-family: ${getThemeFont(['regular'])};
    font-size: ${getThemeSize(['smallText'])};
    font-weight: 400;
    line-height: 14.4px;
    text-align: left;
    color: ${getThemeColor(['darkPrimary'])};
  }
`;
/*Section with styles for working hours. used construction WH on order to name names shorter */

export const WHWrapS = styled(DisplayFlexS)`
  margin-top: 10px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-bottom: 34px;
  }
`;

export const WHDayRowS = styled(DisplayFlexS)`
  margin-bottom: 20px;
`;

export const WHRowCheckboxWrapS = styled.label`
  margin-top: 5px;
  margin-right: 15px;
  max-width: 130px;
  width: 100%;
  cursor: pointer;

  @media (max-width: ${getScreenSize(['medium'])}) {
    position: relative;
    font-family: ${getThemeFont(['regular'])};
    font-size: ${getThemeSize(['middleSmallText'])};
    font-weight: 700;
    display: flex;
    align-items: center;
    margin-top: 14px;
  }
`;

export const WHRowContentS = styled(DisplayFlexS)`
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-top: 22px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;

    & div {
      width: 40%;
      display: flex;
      align-items: center;
      min-width: 66px;
    }
  }
`;

export const WHDayS = styled(Text)`
  margin-left: 10px;
  text-align: left;
`;

export const WHTextS = styled(Text)`
  margin-right: 10px;
  ${({ disabled }) => disabled && `opacity: .5`}

  @media (max-width: ${getScreenSize(['medium'])}) {
    font-size: ${getThemeSize(['middleSmallText'])};
  }
`;

export const WHByAppointmentWrapS = styled.div`
  margin-left: 145px;
  ${({ disabled }) => disabled && `opacity: .5`}
  label {
    cursor: pointer;
  }
  span {
    margin-left: 10px;
  }

  @media (max-width: ${getScreenSize(['medium'])}) {
    position: relative;
    margin: 0;
    label {
      display: flex;
      margin-top: 16px;
      margin-bottom: 18px;
    }
    & span {
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleSmallText'])};
      font-weight: 400;
      margin-left: 13px;
      white-space: nowrap;
    }
  }
`;

export const WHSelectWrS = styled(DisplayFlexS)`
  max-width: 130px;
  width: 100%;
  &:not(:last-of-type) {
    margin-right: 30px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-bottom: 15px;
  }
`;

export const WHSelectS = styled(SelectS)`
  height: 30px;
  background-position-x: 90%;
  &:disabled {
    opacity: 0.5;
  }
`;

export const WHBtnS = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
  }

  @media (max-width: ${getScreenSize(['medium'])}) {
    padding: 0;

    & span {
      font-size: ${getThemeSize(['middleSmallText'])};
    }
  }
`;

export const WHAddIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  background: url(${AddIcon}) no-repeat;
  margin-right: 5px;
`;
export const BlueCloseIcon = styled(WHAddIcon)`
  background: url(${CloseIcon}) no-repeat;
`;

export const SummarySelectionWrapper = styled.div`
  & .summary-label {
    margin-bottom: 10px;
  }

  & div.summary-time-item {
    margin-bottom: 8px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    width: 100%;

    & .summary-label {
      padding-top: 11px;
      border-top: 1px solid #dde9f5;
    }

    & div.summary-time-item {
      margin-bottom: 12px;
      font-size: ${getThemeSize(['middleSmallText'])};

      & b {
        font-size: ${getThemeSize(['middleSmallText'])};
      }
    }
  }
`;

// PicturesSettings
export const PictureSettingsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
`;

export const MultipleMobileImagesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  & span {
    font-family: ${getThemeFont(['regular'])};
    font-size: ${getThemeSize(['smallText'])};
    font-weight: 400;
    line-height: 14.4px;
    text-align: left;
    color: ${getThemeColor(['darkerViolet'])};
  }
  & img {
    width: 12px;
    height: 17px;
  }
`;
export const MultipleMobileImagesEditSectionWrapper = styled.div`
  padding: 16px 22px 4px 16px;
  background: #dde9f5;
  border-radius: 4px;
`;
export const MultipleMobileImagesEditWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px;

  & label.add-image {
    border: 1px solid #1d1d6c;
    &:before {
      width: 27px;
      height: 24px;
      top: calc(50% - 7px);
    }

    &:after {
      content: '${t('companyEdit.pictures.addPhotos')}';
      position: absolute;
      top: calc(50% + 14px);
      left: 50%;
      transform: translate(-50%, -50%);
      font-family: ${getThemeFont(['regular'])};
      font-size: 7px;
      font-weight: 400;
      line-height: 8.4px;
      text-align: left;
      color: ${getThemeColor(['darkerViolet'])};
      white-space: nowrap;
    }
  }

  & label {
    min-width: 66px;
    height: 66px;
    justify-content: space-between;
    margin-bottom: 17px;
    margin-right: 8px;
    margin-left: 8px;
    border-radius: 1px;
  }
`;

export const PicturesSettingsSingleWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 140px;
  & label {
    min-width: 100%;
    height: 198px;

    &:before {
      width: 62px;
      height: 56px;
      top: calc(50% - 14px);
    }

    &:after {
      content: '${t('companyEdit.pictures.addPhotos')}';
      position: absolute;
      top: calc(50% + 30px);
      left: 50%;
      transform: translate(-50%, -50%);
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['smallText'])};
      font-weight: 400;
      line-height: 14.4px;
      text-align: left;
      color: ${getThemeColor(['darkerViolet'])};
    }
  }
`;

export const MobilePicturesSettingsViewWrapper = styled.div`
  position: relative;
  & label:after {
    content: '${(props) => props.imageNumber}';
    width: 15px;
    height: 15px;
    background-color: ${getThemeColor(['red'])};
    font-family: ${getThemeFont(['regular'])};
    font-weight: 700;
    line-height: 10.8px;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    font-family: ${getThemeFont(['regular'])};
    font-size: 9px;
    font-weight: 700;
    line-height: 10.8px;
    text-align: center;
    color: ${getThemeColor(['white'])};
    padding: 2px 0 3px;
  }
`;
export const DeleteImageIcon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  background-color: ${getThemeColor(['red'])};
  border-radius: 50%;
  z-index: 1;
  cursor: pointer;
  margin: 4px;
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    background: url(${DeleteCrossIcon});
    background-size: cover;
    width: 6px;
    height: 6px;
    transform: translate(-50%, -50%);
    background-size: cover;
    background-repeat: no-repeat;
  }
`;

export const PicturesSettingsWrS = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: ${getScreenSize(['medium'])}) {
    justify-content: center;
  }
`;

export const PicturesSettingsRemoveS = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  cursor: auto;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease-in-out;
`;

export const PicturesSettingsRemoveWrS = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  &:before {
    content: '${t('companyEdit.pictures.delete')}';
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 4px 8px;
    font-size: ${getThemeSize(['smallText'])};
    color: ${getThemeColor(['primary'])};
    text-transform: uppercase;
    background-color: ${getThemeColor(['white'])};
    border-radius: 3px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }
  &:hover::before {
    opacity: 1;
  }
`;

export const PicturesSettingsRemoveIconS = styled(FontAwesomeIcon)`
  z-index: 2;
  color: ${getThemeColor(['white'])};
  font-size: ${getThemeSize(['bigText'])};
`;

export const PicturesSettingsItemS = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 14px;
  flex: 0 0 18%;
  max-width: 18%;
  width: 100%;
  height: 115px;
  border-radius: 3px;
  background-color: ${({ active }) => (active ? getThemeColor(['grayBlue']) : '#F5F4F5')};
  cursor: pointer;
  overflow: hidden;
  &:before {
    content: '';
    background: ${({ active }) => (active ? `url(${AddPictureIcon})` : `url(${AddPictureDisabledIcon})`)};
    background-size: cover;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 48px;
    height: 42px;
    background-repeat: no-repeat;
  }
  &:hover {
    ${PicturesSettingsRemoveS} {
      opacity: 1;
      visibility: visible;
    }
  }
`;

export const PicturesSettingsFileS = styled.input`
  width: 100%;
  height: 100%;
  opacity: 0;
  visibility: hidden;
`;

export const PicturesSettingsViewS = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
/* TEAM SECTION STYLES */

export const TeamsHeader = styled.h3`
  font-size: ${getThemeSize(['bigText'])};
  line-height: 19px;
  color: ${getThemeColor(['violet'])};
  font-weight: normal;

  display: block;
  width: 70%;

  padding-bottom: 10px;
  margin-bottom: 15px;
  border-bottom: 1px solid ${getThemeColor(['grayBlue'])};

  @media (max-width: ${getScreenSize(['medium'])}) {
    width: 100%;
    font-family: ${getThemeFont(['regular'])};
    font-size: ${getThemeSize(['middleSmallText'])};
    font-weight: 400;
    line-height: 13.2px;
    text-align: left;
    padding-bottom: 4px;
    margin-bottom: 16px;
  }
`;

export const VerticalLine = styled.div`
  height: 100%;
  width: 1px;
  background-color: ${getThemeColor(['grayBlue'])};
`;

export const EmployeeSectionEditWrapper = styled.div`
  flex-basis: 40%;
  @media (max-width: ${getScreenSize(['medium'])}) {
    width: 100%;
  }
`;

export const TeamsFormS = styled.form`
  @media (max-width: ${getScreenSize(['medium'])}) {
    display: flex;
    flex-direction: column;
    padding-bottom: 18px;
    align-items: center;
  }
`;
export const TeamsFormContentS = styled(DisplayFlexS)`
  justify-content: space-between;
  align-items: stretch;
  padding-bottom: 60px;

  @media (max-width: ${getScreenSize(['medium'])}) {
    display: flex;
    flex-direction: column;
    padding-bottom: 57px;
    align-items: center;
    flex-direction: column;

    &:first-child {
      margin-top: 5px;
    }
  }
`;

export const EmployeeAvatar = styled(LogoWithUploadOverlayS)`
  background: url(${({ avatar }) => avatar || DefaultEmployeeImg}) ${getThemeColor(['grayBlue'])} no-repeat bottom;
  background-size: ${({ avatar }) => (avatar ? 'cover' : '80%')};
  width: 157px;
  height: 186px;
  border-radius: 0;
  margin: 0 0 20px 0;

  @media (max-width: ${getScreenSize(['medium'])}) {
    width: 109px;
    height: 129px;
    margin-bottom: 23px;
    position: relative;
    background: url(${({ avatar }) => avatar || DefaultEmployeeImg}) ${getThemeColor(['grayBlue'])} no-repeat bottom;
    background-size: ${({ avatar }) => (avatar ? 'cover' : '100%')};
  }
`;

export const EditPenIconS = styled.div`
  position: absolute;
  bottom: -9px;
  left: calc(50% - 9px);
  background: #ffc6d7;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  justify-content: center;
  align-items: center;

  & img {
    width: 9px;
    height: 9px;
  }
`;

export const LargeCheckBoxWrapS = styled.div`
  margin-top: 8px;
`;

export const LargeCheckBoxOptionS = styled.span`
  font-size: ${getThemeSize(['normalText'])};
  color: ${getThemeColor(['darkerViolet'])};
  margin-left: 8px;
  cursor: pointer;

  @media (max-width: ${getScreenSize(['medium'])}) {
    font-size: ${getThemeSize(['middleSmallText'])};
  }
`;

export const EditContentWrapper = styled.div`
  @media (max-width: ${getScreenSize(['medium'])}) {
    padding: 0 24px;
    margin-bottom: 125px;
  }
`;

export const BaseBlockStyled = styled.div`
  margin-bottom: 20px;
  padding: 20px 0;
  border-radius: 3px;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);

  background-color: ${getThemeColor(['white'])};

  @media (max-width: ${getScreenSize(['medium'])}) {
    padding: 17px 0 0;
  }
`;

export const PasswordButtonsWrapper = styled.div`
  & button {
    margin: 20px 0;
  }

  & button:first {
    margin-right: 15px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    margin-top: 15px;

    & button {
      margin: 0;
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleSmallText'])};
      font-weight: 700;
      color: ${getThemeColor(['violet'])};
      padding: 12px;
      min-width: 168px;
    }
  }
`;

export const PlanButtonsWrapper = styled.div`
  
  & button:first {
    margin-right: 15px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {

    & button {
      margin: 5px 0 0 0;
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleSmallText'])};
      font-weight: 700;
      color: ${getThemeColor(['violet'])};
      padding: 12px;
      min-width: 168px;
    }
  }
`;

export const SaveButtonWrapper = styled.div`
  margin-top: ${({ marginTop }) => (marginTop ? `${marginTop}px` : '30px')};

  @media (max-width: ${getScreenSize(['medium'])}) {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: ${({ marginTopMobile }) => (marginTopMobile ? `${marginTopMobile}px` : '90px')};
  }
`;

export const CompanyRateEditWrapper = styled.div`
  & p.rate-description {
    margin-bottom: 30px;
    line-height: 19px;
  }
  @media (max-width: ${getScreenSize(['medium'])}) {
    & p.rate-description {
      font-family: ${getThemeFont(['regular'])};
      font-size: ${getThemeSize(['middleSmallText'])};
      font-weight: 400;
      line-height: 16px;
      margin-bottom: 14px;
    }
    & ${InputWrapper} {
      span {
        font-family: ${getThemeFont(['regular'])};
        font-size: 11px;
        font-weight: 400;
        line-height: 13.2px;
        text-align: left;
      }
    }
  }
`;
export const CompanyMainInformationEditWrapper = styled.div`
  @media (max-width: ${getScreenSize(['medium'])}) {
    & ${InputWrapper}:first-of-type {
      margin-bottom: 19px;
      min-width: 100%;
    }

    & form {
      width: 100%;
      height: 100%;
    }
  }
`;
